import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
// Marketing / SEO pages stay eager (lightweight, share chrome, most important to index fast).
import LandingPage from "@/pages/LandingPage";
import NoticeTypesHub from "@/pages/NoticeTypesHub";
import CategoryLandingPage from "@/pages/CategoryLandingPage";
import NoticeLandingPage from "@/pages/NoticeLandingPage";

// Heavier app flows split into their own chunks so marketing pages ship less JS.
const NoticeWizard = lazy(() => import("@/pages/NoticeWizard"));
const NoticeSent = lazy(() => import("@/pages/NoticeSent"));
const TrackNotice = lazy(() => import("@/pages/TrackNotice"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cream text-navy" data-testid="route-loading">
      Loading…
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <RouteFallback />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/create" element={<NoticeWizard />} />
                <Route path="/notice-sent/:ref" element={<NoticeSent />} />
                <Route path="/track" element={<TrackNotice />} />
                <Route path="/legal-notices" element={<NoticeTypesHub />} />
                <Route path="/legal-notices/:category" element={<CategoryLandingPage />} />
                <Route path="/legal-notice/:slug" element={<NoticeLandingPage />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
